import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Play, Trash, CircleNotch, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const PC = { gpt: "#10A37F", claude: "#D97757", gemini: "#8E24AA" };
const PL = { gpt: "GPT", claude: "Claude", gemini: "Gemini" };
const INTERVALS = [
  { v: 60, l: "Every hour" },
  { v: 360, l: "Every 6 hours" },
  { v: 720, l: "Every 12 hours" },
  { v: 1440, l: "Daily" },
  { v: 10080, l: "Weekly" },
];

export default function Queries() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(null);
  const [form, setForm] = useState({
    name: "", brand: "", prompt: "", providers: ["gpt", "claude", "gemini"], interval_minutes: 1440,
  });

  const { data: queries, isLoading } = useQuery({
    queryKey: ["queries"], queryFn: async () => (await api.get("/queries")).data,
  });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/queries", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["queries"] });
      setOpen(false);
      setForm({ name: "", brand: "", prompt: "", providers: ["gpt", "claude", "gemini"], interval_minutes: 1440 });
      toast.success("Query created");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.put(`/queries/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["queries"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/queries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["queries"] });
      toast.success("Query deleted");
    },
  });

  const runNow = async (id, name) => {
    setRunning(id);
    toast.info(`Probing LLMs for "${name}"…`);
    try {
      const { data } = await api.post(`/queries/${id}/run`);
      toast.success(data.message);
      qc.invalidateQueries({ queryKey: ["queries"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      qc.invalidateQueries({ queryKey: ["trends"] });
      qc.invalidateQueries({ queryKey: ["citations-feed"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRunning(null);
    }
  };

  const toggleProvider = (p) => {
    setForm((f) => ({
      ...f,
      providers: f.providers.includes(p) ? f.providers.filter((x) => x !== p) : [...f.providers, p],
    }));
  };

  const intervalLabel = (m) => INTERVALS.find((i) => i.v === m)?.l || `Every ${m} min`;

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8 flex items-end justify-between fade-in">
        <div>
          <p className="overline text-action">Configuration</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter">Tracked Queries</h1>
          <p className="mt-1 text-sm text-muted-foreground">Prompts probed on a schedule to measure brand citations.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-action text-white hover:bg-action/90" data-testid="new-query-button">
              <Plus size={16} weight="bold" /> New Query
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border bg-card sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Create tracked query</DialogTitle>
              <DialogDescription>Probe LLMs on a schedule to measure how often your brand is cited.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Query name</Label>
                  <Input data-testid="query-name" value={form.name} placeholder="No-code builders"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Brand / keyword</Label>
                  <Input data-testid="query-brand" value={form.brand} placeholder="Emergent"
                    onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Prompt to probe</Label>
                <Textarea data-testid="query-prompt" rows={3} value={form.prompt}
                  placeholder="What are the best AI app builders in 2026?"
                  onChange={(e) => setForm({ ...form, prompt: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Providers</Label>
                <div className="flex gap-2">
                  {Object.keys(PC).map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`provider-toggle-${p}`}
                      onClick={() => toggleProvider(p)}
                      className={`rounded-md border px-3 py-1.5 text-sm transition-colors duration-200 ${
                        form.providers.includes(p)
                          ? "border-transparent text-white"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                      style={form.providers.includes(p) ? { background: PC[p] } : {}}
                    >
                      {PL[p]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Schedule</Label>
                <Select value={String(form.interval_minutes)}
                  onValueChange={(v) => setForm({ ...form, interval_minutes: Number(v) })}>
                  <SelectTrigger data-testid="query-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INTERVALS.map((i) => (
                      <SelectItem key={i.v} value={String(i.v)}>{i.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                className="w-full bg-action text-white hover:bg-action/90"
                data-testid="submit-query"
                disabled={createMut.isPending || !form.name || !form.brand || !form.prompt || !form.providers.length}
                onClick={() => createMut.mutate(form)}
              >
                {createMut.isPending ? <CircleNotch size={18} className="animate-spin" /> : "Create query"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="queries-list">
          {(queries || []).map((q) => (
            <Card key={q.id} className="border-border bg-card p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-lg font-bold tracking-tight">{q.name}</h3>
                    <Badge variant="outline" className="border-border text-xs text-muted-foreground">{q.brand}</Badge>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{q.prompt}</p>
                </div>
                <Switch
                  checked={q.active}
                  data-testid={`query-active-${q.id}`}
                  onCheckedChange={(v) => updateMut.mutate({ id: q.id, body: { active: v } })}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {q.providers.map((p) => (
                  <span key={p} className="flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs" style={{ color: PC[p] }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: PC[p] }} /> {PL[p]}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-border/40 pt-4">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock size={13} /> {intervalLabel(q.interval_minutes)}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                    data-testid={`delete-query-${q.id}`}
                    onClick={() => deleteMut.mutate(q.id)}>
                    <Trash size={15} />
                  </Button>
                  <Button size="sm" className="gap-1.5 bg-action text-white hover:bg-action/90"
                    data-testid={`run-query-${q.id}`}
                    disabled={running === q.id}
                    onClick={() => runNow(q.id, q.name)}>
                    {running === q.id ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} weight="fill" />}
                    Run now
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {(!queries || queries.length === 0) && (
            <p className="text-sm text-muted-foreground">No queries yet. Create one to start tracking.</p>
          )}
        </div>
      )}
    </div>
  );
}
